import { apiService } from './api';

interface SessionData {
  sessionType?: string;
  browserName: string;
  browserVersion: string;
  browserVendor: string;
  webAppVersion?: string;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  deviceType: string;
  isMobile: boolean;
  isTouchDevice: boolean;
  userAgent: string;
  referrer: string;
  currentUrl: string;
  isAuthenticated: boolean;
  authMethod?: string | null;
  countryCode?: string;
  city?: string;
  region?: string;
  timezone?: string;
  languageCode?: string;
  metadata?: Record<string, any>;
}

/**
 * Webplay Session Service
 * Handles session tracking for webplay web application
 */
class WebplaySessionService {
  private currentSessionId: string | null = null;
  private totalVisibleTime: number = 0;
  private visibilityChanges: number = 0;
  private lastVisibilityTime: number = 0;
  private isVisible: boolean = true;
  private sessionCreationPromise: Promise<string | null> | null = null;

  /**
   * Detect browser name from user agent
   */
  private getBrowserName(userAgent: string): string {
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
    return 'Unknown';
  }

  /**
   * Extract browser version from user agent
   */
  private getBrowserVersion(userAgent: string): string {
    const match = userAgent.match(/(Chrome|Safari|Firefox|Edg|OPR)\/(\d+\.\d+)/);
    return match ? match[2] : 'Unknown';
  }

  /**
   * Detect device type from user agent
   */
  private getDeviceType(userAgent: string): string {
    if (/tablet|ipad/i.test(userAgent)) return 'tablet';
    if (/mobile|android|iphone/i.test(userAgent)) return 'mobile';
    return 'desktop';
  }

  /**
   * Collect session data from browser
   */
  private collectSessionData(): SessionData {
    const userAgent = navigator.userAgent;
    const isAuthenticated = !!localStorage.getItem('beemi_webplay_access_token');
    
    // Try to get auth method from stored user data
    let authMethod: string | null = null;
    try {
      const userData = localStorage.getItem('beemi_webplay_user');
      if (userData) {
        const parsed = JSON.parse(userData);
        authMethod = parsed.authMethod || null;
      }
    } catch (error) {
      console.error('[WebplaySession] Failed to parse user data:', error);
    }

    return {
      sessionType: 'webplay_launch',
      browserName: this.getBrowserName(userAgent),
      browserVersion: this.getBrowserVersion(userAgent),
      browserVendor: navigator.vendor || 'Unknown',
      webAppVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      pixelRatio: window.devicePixelRatio || 1,
      deviceType: this.getDeviceType(userAgent),
      isMobile: /mobile|android|iphone/i.test(userAgent),
      isTouchDevice: 'ontouchstart' in window,
      userAgent,
      referrer: document.referrer || '',
      currentUrl: window.location.href,
      isAuthenticated,
      authMethod,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      languageCode: navigator.language,
      metadata: {
        timestamp: new Date().toISOString()
      }
    };
  }

  /**
   * Log a new session
   */
  async logSession(): Promise<string | null> {
    // If there's already a session creation in progress, wait for it
    if (this.sessionCreationPromise) {
      console.log('📊 [WebplaySession] Session creation already in progress, waiting...');
      return this.sessionCreationPromise;
    }

    // Check if we already have a session ID in sessionStorage
    const existingSessionId = sessionStorage.getItem('webplay_session_id');
    if (existingSessionId) {
      console.log('📊 [WebplaySession] Reusing existing session:', existingSessionId);
      this.currentSessionId = existingSessionId;
      this.lastVisibilityTime = Date.now();
      this.isVisible = !document.hidden;
      return existingSessionId;
    }

    // Create the session creation promise to prevent race conditions
    this.sessionCreationPromise = (async () => {
      try {
        console.log('📊 [WebplaySession] Logging new session start...');
        
        const sessionData = this.collectSessionData();
        this.lastVisibilityTime = Date.now();
        this.isVisible = !document.hidden;
        
        const response = await apiService.post('/webplay/sessions', sessionData);
        
        if (response.data.success && response.data.data?.sessionId) {
          this.currentSessionId = response.data.data.sessionId;
          // Persist session ID to sessionStorage (cleared when browser tab closes)
          // TypeScript note: currentSessionId is guaranteed non-null here since we just assigned it
          sessionStorage.setItem('webplay_session_id', this.currentSessionId as string);
          console.log('✅ [WebplaySession] New session started:', this.currentSessionId);
          return this.currentSessionId;
        }
        
        console.warn('⚠️ [WebplaySession] No session ID in response');
        return null;
      } catch (error) {
        console.error('❌ [WebplaySession] Failed to log session:', error);
        // Generate a temporary local session ID to prevent infinite retries
        // This allows the app to continue working even if the server is unreachable
        const tempSessionId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        console.log('📊 [WebplaySession] Using temporary offline session:', tempSessionId);
        this.currentSessionId = tempSessionId;
        sessionStorage.setItem('webplay_session_id', tempSessionId);
        return tempSessionId;
      } finally {
        // Clear the promise after completion
        this.sessionCreationPromise = null;
      }
    })();

    return this.sessionCreationPromise;
  }

  /**
   * End the current session
   * Note: This is typically called during page unload (beforeunload event)
   */
  async endSession(): Promise<void> {
    if (!this.currentSessionId) {
      return;
    }

    // Don't try to end temporary offline sessions (they were never created on the server)
    if (this.currentSessionId.startsWith('temp-')) {
      console.log('📊 [WebplaySession] Skipping end for temporary offline session:', this.currentSessionId);
      sessionStorage.removeItem('webplay_session_id');
      this.currentSessionId = null;
      return;
    }

    const sessionIdToEnd = this.currentSessionId;

    try {
      console.log('🏁 [WebplaySession] Ending session:', sessionIdToEnd);
      
      // Calculate final visible time
      if (this.isVisible) {
        const now = Date.now();
        this.totalVisibleTime += (now - this.lastVisibilityTime);
      }
      
      const endData = {
        totalVisibleTimeMs: this.totalVisibleTime,
        visibilityChanges: this.visibilityChanges
      };

      // Use sendBeacon API for reliable transmission during page unload
      // sendBeacon is specifically designed to work even when the page is unloading
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const cleanBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
      const url = `${cleanBaseUrl}/api/webplay/sessions/${sessionIdToEnd}/end`;
      
      // sendBeacon requires a Blob for JSON data
      const blob = new Blob([JSON.stringify(endData)], { type: 'application/json' });
      
      // Try sendBeacon first (more reliable during page unload)
      if (navigator.sendBeacon) {
        const sent = navigator.sendBeacon(url, blob);
        if (sent) {
          console.log('✅ [WebplaySession] Session end queued via sendBeacon');
        } else {
          console.warn('⚠️ [WebplaySession] sendBeacon failed, trying fallback');
          // Fallback to regular POST (less reliable during unload but worth trying)
          await apiService.post(`/webplay/sessions/${sessionIdToEnd}/end`, endData);
        }
      } else {
        // Browser doesn't support sendBeacon, use regular POST
        await apiService.post(`/webplay/sessions/${sessionIdToEnd}/end`, endData);
        console.log('✅ [WebplaySession] Session ended via POST (no sendBeacon support)');
      }
    } catch (error) {
      // Fail silently during page unload - this is expected and normal
      // The session will eventually timeout on the server side
      console.error('❌ [WebplaySession] Failed to end session (expected during page unload):', error);
    } finally {
      // Always clear the session ID from storage
      sessionStorage.removeItem('webplay_session_id');
      this.currentSessionId = null;
    }
  }

  /**
   * Track visibility change
   */
  trackVisibilityChange(isVisible: boolean): void {
    const now = Date.now();
    
    if (this.isVisible && !isVisible) {
      // Going hidden - accumulate visible time
      this.totalVisibleTime += (now - this.lastVisibilityTime);
    } else if (!this.isVisible && isVisible) {
      // Coming back visible - reset timer
      this.lastVisibilityTime = now;
    }
    
    this.isVisible = isVisible;
    this.visibilityChanges++;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Get total visible time
   */
  getTotalVisibleTime(): number {
    if (this.isVisible) {
      return this.totalVisibleTime + (Date.now() - this.lastVisibilityTime);
    }
    return this.totalVisibleTime;
  }

  /**
   * Get visibility change count
   */
  getVisibilityChanges(): number {
    return this.visibilityChanges;
  }
}

// Export singleton instance
export const webplaySessionService = new WebplaySessionService();
export default webplaySessionService;
